<template>
  <div class="asignar-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="asignar-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div>
          <h1 class="page-title">Asignar rutina a miembro</h1>
          <p class="page-sub">Programa el entrenamiento de tus miembros</p>
        </div>
        <button class="btn-ghost" @click="$router.go(-1)">
          <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
          Volver
        </button>
      </header>

      <!-- Selección de miembro y rutina -->
      <section class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Seleccionar miembro y rutina
            </span>
          </div>
          <div class="card-flat-body">
            <div class="field-row">
              <div class="field">
                <label class="field-label">Miembro *</label>
                <v-autocomplete v-model="asignacion.miembro_id" :items="miembros" item-text="text" item-value="id"
                  :rules="[v => !!v || 'El miembro es obligatorio']" outlined dense hide-details="auto"
                  placeholder="Buscar miembro…" class="dark-input"></v-autocomplete>
              </div>
              <div class="field">
                <label class="field-label">Rutina *</label>
                <v-autocomplete v-model="asignacion.rutina_id" :items="rutinas" item-text="nombre" item-value="id"
                  :rules="[v => !!v || 'La rutina es obligatoria']" outlined dense hide-details="auto"
                  placeholder="Buscar rutina…" class="dark-input"></v-autocomplete>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Información de la rutina seleccionada -->
      <section v-if="rutinaSeleccionada" class="fade-in delay-200 mt-lg">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              {{ rutinaSeleccionada.nombre }}
              <span class="nivel-badge ml-2" :class="`nivel-${rutinaSeleccionada.nivel_dificultad}`">
                {{ rutinaSeleccionada.nivel_dificultad }}
              </span>
            </span>
          </div>
          <div class="card-flat-body">
            <div class="info-grid">
              <div class="info-card">
                <v-icon size="16" color="#C8FF4D">mdi-target</v-icon>
                <div>
                  <div class="info-label">Objetivo</div>
                  <div class="info-value">{{ formatObjetivo(rutinaSeleccionada.objetivo) }}</div>
                </div>
              </div>
              <div class="info-card">
                <v-icon size="16" color="#C8FF4D">mdi-calendar-range</v-icon>
                <div>
                  <div class="info-label">Duración</div>
                  <div class="info-value">{{ rutinaSeleccionada.duracion_semanas }} semanas</div>
                </div>
              </div>
              <div class="info-card">
                <v-icon size="16" color="#C8FF4D">mdi-calendar-week</v-icon>
                <div>
                  <div class="info-label">Días por semana</div>
                  <div class="info-value">{{ rutinaSeleccionada.total_dias }}</div>
                </div>
              </div>
              <div class="info-card" v-if="rutinaSeleccionada.descripcion">
                <v-icon size="16" color="#C8FF4D">mdi-text</v-icon>
                <div>
                  <div class="info-label">Descripción</div>
                  <div class="info-value">{{ rutinaSeleccionada.descripcion }}</div>
                </div>
              </div>
            </div>

            <div class="info-box mt-lg">
              <v-icon size="16" color="#8C8C93" class="mr-2">mdi-information-outline</v-icon>
              Esta rutina contiene <strong>{{ totalEjercicios }} ejercicios</strong> distribuidos en
              <strong>{{ rutinaSeleccionada.total_dias }} días</strong> por semana.
            </div>
          </div>
        </div>
      </section>

      <!-- Configuración de la asignación -->
      <section class="fade-in delay-300 mt-lg">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Configuración de la asignación
            </span>
          </div>
          <div class="card-flat-body">
            <div class="field-row">
              <div class="field">
                <label class="field-label">Fecha de inicio *</label>
                <v-menu v-model="menuFechaInicio" :close-on-content-click="false" transition="scale-transition"
                  offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="asignacion.fecha_inicio" readonly outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on" class="dark-input"></v-text-field>
                  </template>
                  <v-date-picker v-model="asignacion.fecha_inicio" @input="menuFechaInicio = false" :min="fechaHoy"
                    class="dark-picker"></v-date-picker>
                </v-menu>
              </div>
              <div class="field">
                <label class="field-label">Duración (semanas) *</label>
                <v-text-field v-model="asignacion.duracion_semanas" type="number" min="1" outlined dense
                  hide-details="auto" class="dark-input"></v-text-field>
              </div>
            </div>

            <div class="field">
              <label class="field-label">Notas para el miembro</label>
              <v-textarea v-model="asignacion.notas" outlined dense rows="3" hide-details="auto"
                placeholder="Instrucciones especiales, recomendaciones, etc." class="dark-input"></v-textarea>
            </div>
          </div>
        </div>
      </section>

      <!-- Opciones de entrega -->
      <section class="fade-in delay-400 mt-lg">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Opciones de entrega
            </span>
          </div>
          <div class="card-flat-body">
            <div class="checkbox-list">
              <label class="check-row">
                <input type="checkbox" v-model="opcionesEntrega.enviarEmail" class="check-input" />
                <span class="check-label">Enviar por correo electrónico</span>
              </label>
              <label class="check-row">
                <input type="checkbox" v-model="opcionesEntrega.generarPDF" class="check-input" />
                <span class="check-label">Generar PDF para descarga</span>
              </label>
              <label class="check-row check-row-disabled">
                <input type="checkbox" checked disabled class="check-input" />
                <span class="check-label">Mostrar en versión web interactiva</span>
              </label>
            </div>

            <div v-if="opcionesEntrega.enviarEmail" class="info-box mt-lg">
              <v-icon size="16" color="#8C8C93" class="mr-2">mdi-email-outline</v-icon>
              Se enviará un correo con los detalles de la rutina y enlaces a los videos demostrativos.
            </div>

            <div v-if="opcionesEntrega.generarPDF" class="info-box mt-md">
              <v-icon size="16" color="#8C8C93" class="mr-2">mdi-file-pdf-box</v-icon>
              Se generará un PDF con la rutina completa, incluyendo imágenes de los ejercicios.
            </div>
          </div>
        </div>
      </section>

      <!-- Botón de asignación -->
      <section class="fade-in delay-500 mt-lg">
        <button class="btn-primary btn-block" @click="asignarRutina" :disabled="!formValido || asignando">
          <span v-if="!asignando">
            <v-icon size="15" class="mr-2">mdi-check</v-icon>
            Asignar rutina
          </span>
          <span v-else class="loading-inline"><span class="spinner-sm"></span>Asignando</span>
        </button>
      </section>
    </div>

    <!-- Loading Overlay -->
    <div v-if="cargando" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando…</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="5000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn v-if="mensaje.urlPdf" color="white" text v-bind="attrs" @click="descargarPDF">
          Descargar PDF
        </v-btn>
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import jsPDF from 'jspdf'

export default {
  name: "AsignarRutina",

  data: () => ({
    cargando: false,
    asignando: false,
    menuFechaInicio: false,
    miembros: [],
    rutinas: [],
    rutinaSeleccionada: null,
    asignacion: {
      miembro_id: null,
      rutina_id: null,
      fecha_inicio: new Date().toISOString().substr(0, 10),
      duracion_semanas: 4,
      notas: '',
      usuario_id: localStorage.getItem('idUsuario')
    },
    opcionesEntrega: {
      enviarEmail: true,
      generarPDF: true,
      mostrarWeb: true
    },
    mostrarMensaje: false,
    mensaje: { texto: '', color: '', urlPdf: '' },
    fechaHoy: new Date().toISOString().substr(0, 10)
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        default: return 'mdi-information'
      }
    },

    formValido() {
      return this.asignacion.miembro_id &&
        this.asignacion.rutina_id &&
        this.asignacion.fecha_inicio &&
        this.asignacion.duracion_semanas > 0;
    },

    totalEjercicios() {
      if (!this.rutinaSeleccionada || !this.rutinaSeleccionada.dias) return 0;
      return this.rutinaSeleccionada.dias.reduce((total, dia) => {
        const ejerciciosLength = dia.ejercicios ? dia.ejercicios.length : 0;
        return total + ejerciciosLength;
      }, 0);
    }
  },

  mounted() {
    this.cargarMiembros();
    this.cargarRutinas();
  },

  watch: {
    'asignacion.rutina_id': function (nuevoId) {
      if (nuevoId) {
        this.cargarDetallesRutina(nuevoId);
      }
    }
  },

  methods: {
    async cargarMiembros() {
      this.cargando = true;
      try {
        const respuesta = await HttpService.obtenerConDatos(
          { metodo: "obtener_miembros_disponibles" },
          "miembros.php"
        );
        this.miembros = respuesta.map(m => ({
          id: m.id,
          text: `Matrícula: ${m.matricula} - ${m.nombre}`
        }));
      } catch (error) {
        console.error("Error cargando miembros:", error);
        this.mostrarError("Error al cargar los miembros");
      } finally {
        this.cargando = false;
      }
    },

    async cargarRutinas() {
      try {
        const respuesta = await HttpService.obtenerConDatos(
          { metodo: "get" },
          "rutinas.php"
        );
        this.rutinas = respuesta;
      } catch (error) {
        console.error("Error cargando rutinas:", error);
        this.mostrarError("Error al cargar las rutinas");
      }
    },

    async cargarDetallesRutina(id) {
      try {
        const respuesta = await HttpService.obtenerConDatos(
          { metodo: "get_by_id", id: id },
          "rutinas.php"
        );
        this.rutinaSeleccionada = respuesta;
      } catch (error) {
        console.error("Error cargando detalles de rutina:", error);
        this.mostrarError("Error al cargar los detalles de la rutina");
      }
    },

    async asignarRutina() {
      this.asignando = true;

      try {
        const payload = {
          metodo: "asignar",
          asignacion: {
            ...this.asignacion,
            fecha_fin: this.calcularFechaFin()
          }
        };

        const resultado = await HttpService.registrar(payload, "miembro_rutina.php");

        if (resultado.success) {
          if (this.opcionesEntrega.generarPDF) {
            await this.generarPDF();
          }

          if (this.opcionesEntrega.enviarEmail) {
            await this.enviarEmail(resultado.id);
          }

          this.mostrarExito('✅ Rutina asignada exitosamente');
          this.limpiarFormulario();
        } else {
          throw new Error(resultado.message || 'Error desconocido');
        }
      } catch (error) {
        this.mostrarError('❌ Error al asignar la rutina: ' + error.message);
      } finally {
        this.asignando = false;
      }
    },

    calcularFechaFin() {
      const fechaInicio = new Date(this.asignacion.fecha_inicio);
      fechaInicio.setDate(fechaInicio.getDate() + (this.asignacion.duracion_semanas * 7));
      return fechaInicio.toISOString().substr(0, 10);
    },

    limpiarFormulario() {
      this.asignacion = {
        miembro_id: null,
        rutina_id: null,
        fecha_inicio: new Date().toISOString().substr(0, 10),
        duracion_semanas: 4,
        notas: '',
        usuario_id: localStorage.getItem('idUsuario')
      };
      this.rutinaSeleccionada = null;
      this.opcionesEntrega = {
        enviarEmail: true,
        generarPDF: true,
        mostrarWeb: true
      };
    },

    async generarPDF() {
      try {
        const doc = new jsPDF();

        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text(`RUTINA DE ENTRENAMIENTO`, 105, 20, { align: 'center' });

        doc.setFontSize(16);
        doc.text(this.rutinaSeleccionada.nombre, 105, 30, { align: 'center' });

        doc.setFontSize(12);
        doc.text(`Miembro: ${this.obtenerNombreMiembro()}`, 20, 50);
        doc.text(`Fecha inicio: ${this.asignacion.fecha_inicio}`, 20, 60);
        doc.text(`Duración: ${this.asignacion.duracion_semanas} semanas`, 20, 70);
        doc.text(`Objetivo: ${this.formatObjetivo(this.rutinaSeleccionada.objetivo)}`, 20, 80);

        doc.line(20, 90, 190, 90);

        let y = 100;
        this.rutinaSeleccionada.dias.forEach((dia, index) => {
          if (y > 250) {
            doc.addPage();
            y = 20;
          }

          doc.setFontSize(14);
          doc.setTextColor(0, 0, 0);
          doc.text(`DÍA ${index + 1}: ${dia.nombre_dia}`, 20, y);
          y += 10;

          doc.setFontSize(12);
          dia.ejercicios.forEach((ejercicio, ejIndex) => {
            if (y > 270) {
              doc.addPage();
              y = 20;
            }

            doc.text(`${ejIndex + 1}. ${ejercicio.ejercicio_nombre}`, 25, y);
            doc.text(`   Series: ${ejercicio.series} | Reps: ${ejercicio.repeticiones} | Descanso: ${ejercicio.descanso_segundos}s`, 25, y + 5);

            if (ejercicio.peso_recomendado) {
              doc.text(`   Peso: ${ejercicio.peso_recomendado}`, 25, y + 10);
              y += 5;
            }

            y += 15;
          });

          y += 10;
        });

        if (this.asignacion.notas) {
          if (y > 250) {
            doc.addPage();
            y = 20;
          }

          doc.setFontSize(12);
          doc.text(`Notas: ${this.asignacion.notas}`, 20, y);
        }

        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        this.mensaje.urlPdf = url;

        window.open(url, '_blank');
      } catch (error) {
        console.error("Error generando PDF:", error);
      }
    },

    async enviarEmail(asignacionId) {
      try {
        const payload = {
          metodo: "enviar_rutina",
          miembro_id: this.asignacion.miembro_id,
          rutina_id: this.asignacion.rutina_id,
          asignacion_id: asignacionId
        };

        const resultado = await HttpService.registrar(payload, "correo.php");

        if (resultado && resultado.success) {
          console.log("Email enviado correctamente");
        } else {
          console.warn("Error enviando email:", resultado.message);
        }
      } catch (error) {
        console.error("Error enviando email:", error);
      }
    },

    obtenerNombreMiembro() {
      const miembro = this.miembros.find(m => m.id === this.asignacion.miembro_id);
      return miembro ? miembro.text : 'Miembro';
    },

    descargarPDF() {
      const link = document.createElement('a');
      link.href = this.mensaje.urlPdf;
      link.download = `rutina-${this.rutinaSeleccionada.nombre}.pdf`;
      link.click();
    },

    mostrarExito(mensaje) {
      this.mensaje = { texto: mensaje, color: "success", urlPdf: this.mensaje.urlPdf };
      this.mostrarMensaje = true;
    },

    mostrarError(mensaje) {
      this.mensaje = { texto: mensaje, color: "error" };
      this.mostrarMensaje = true;
    },

    formatObjetivo(objetivo) {
      const objetivos = {
        'Perdida_grasa': 'Pérdida de grasa',
        'Ganancia_muscular': 'Ganancia muscular',
        'Fuerza': 'Fuerza',
        'Resistencia': 'Resistencia',
        'General': 'General'
      };
      return objetivos[objetivo] || objetivo;
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.asignar-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.asignar-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.asignar-shell {
  position: relative;
  z-index: 1;
  max-width: 820px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.card-flat-head {
  padding: 18px 24px;
  border-bottom: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.card-flat-body {
  padding: 24px;
}

/* ===== FIELDS ===== */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field:not(:last-child) {
  margin-bottom: 16px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

/* ===== INFO GRID ===== */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-label {
  font-size: 10.5px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.info-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  margin-top: 2px;
}

.info-box {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 14px;
  font-size: 12.5px;
  color: var(--text-secondary);
}

.info-box strong {
  color: var(--text-primary);
}

.nivel-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius);
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.nivel-principiante {
  background: var(--accent-dim);
  color: var(--accent);
}

.nivel-intermedio {
  background: rgba(255, 184, 77, 0.12);
  color: var(--warning);
}

.nivel-avanzado {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

/* ===== CHECKBOXES ===== */
.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.check-row-disabled {
  cursor: default;
  opacity: 0.5;
}

.check-input {
  width: 16px;
  height: 16px;
  accent-color: var(--accent);
  cursor: pointer;
}

.check-row-disabled .check-input {
  cursor: default;
}

.check-label {
  font-size: 13px;
  color: var(--text-primary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 13px 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-block {
  width: 100%;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ===== LOADING ===== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }
.delay-300 { animation-delay: 0.15s; }
.delay-400 { animation-delay: 0.2s; }
.delay-500 { animation-delay: 0.25s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner, .spinner-sm { animation: none; }
}

/* ===== Overrides Vuetify ===== */
.dark-input >>> .v-input__slot {
  background: var(--bg) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> textarea {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 14px !important;
  caret-color: var(--accent) !important;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-input >>> .v-messages {
  color: var(--error) !important;
}

.asignar-page >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.asignar-page >>> .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.asignar-page >>> .v-list-item {
  border-radius: 4px !important;
}

.asignar-page >>> .v-list-item__title {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13px !important;
}

.asignar-page >>> .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.asignar-page >>> .v-list-item--active {
  background: var(--accent-dim) !important;
}

.dark-picker {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header,
.dark-picker >>> .v-picker__body,
.dark-picker >>> .v-date-picker-table {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header button,
.dark-picker >>> .v-date-picker-header .v-icon,
.dark-picker >>> .v-date-picker-table th {
  color: var(--text-primary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn {
  color: var(--text-secondary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn.v-btn--active {
  background: var(--accent) !important;
  color: var(--bg) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 600px) {
  .field-row {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Planes/AsignarRutina.vue